import styled from '@emotion/styled';
import { Colors, Flex, Text } from '@revenuemarket/cds';
import { Modal, ModalProps } from 'antd';
import dayjs from 'dayjs';

import { IItemListItem, IStoreInfo, TAX_INVOICE_TYPE } from '~apis/tax-invoice';

import { PurposeData, PurposeType, TaxationData, TaxationType } from './type';

interface Props extends ModalProps {
  supplierInfo: Partial<IStoreInfo>;
  contractorInfo: Partial<IStoreInfo>;
  itemList: IItemListItem[];
  approvalNo?: string;
  writtenDate: string;
  supplyValue?: number;
  taxAmount?: number;
  totalAmount?: number;
  type?: TAX_INVOICE_TYPE;
  taxationType: TaxationType;
  modificationReason?: string;
  remark?: string;
  purposeType?: PurposeType | null;
}

const Container = styled.div`
  border: 2px solid ${Colors.blue500};
  background: ${Colors.white};
  font-size: 12px;
`;

const Header = styled.div`
  display: flex;
  border-bottom: 1px solid ${Colors.gray300};
`;

const TitleSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-right: 1px solid ${Colors.gray300};
`;

const ApprovalSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 8px 12px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid ${Colors.gray300};
`;

const SupplierSection = styled.div`
  flex: 1;
  display: flex;
  border: 2px solid #ffcac2;
  background: #ffeeeb;
`;

const ContractorSection = styled.div`
  flex: 1;
  display: flex;
  border: 2px solid #bfc7ff;
  background: #e8ebff;
`;

const SectionHeader = styled.div<{ color: string }>`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  padding: 8px 4px;
  background: ${({ color }) => color};
  color: ${Colors.white};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
`;

const InfoGrid = styled.div`
  display: flex;
  flex: 1;
`;

const InfoTable = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const InfoTableRow = styled.div`
  display: flex;
  flex: 1;
  border-bottom: 1px solid ${Colors.gray200};
  min-height: 28px;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.div<{ bgColor?: string; textColor?: string }>`
  width: 60px;
  padding: 4px 6px;
  background: ${({ bgColor }) => bgColor || Colors.gray50};
  border-right: 1px solid ${Colors.gray200};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: ${({ textColor }) => textColor || Colors.gray700};
  text-align: center;
`;

const InfoValue = styled.div`
  flex: 1;
  padding: 4px 6px;
  display: flex;
  align-items: center;
  font-size: 11px;
  word-break: break-all;
  background: ${Colors.white};
`;

const TransactionRow = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${Colors.gray300};
`;

const TransactionRowInner = styled.div`
  display: flex;
  flex: 1;
`;

const TransactionCell = styled.div<{ flex?: number; header?: boolean }>`
  flex: ${({ flex }) => flex || 1};
  padding: 6px 8px;
  border-right: 1px solid ${Colors.gray200};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ header }) => (header ? Colors.gray50 : Colors.white)};
  font-size: 11px;

  &:last-child {
    border-right: none;
  }
`;

const ItemTable = styled.div`
  border-bottom: 1px solid ${Colors.gray300};
`;

const ItemTableHeader = styled.div`
  display: flex;
  background: ${Colors.gray50};
  border-bottom: 1px solid ${Colors.gray200};
`;

const ItemTableCell = styled.div<{ width?: string; align?: string }>`
  width: ${({ width }) => width || 'auto'};
  flex: ${({ width }) => (width ? 'none' : 1)};
  padding: 6px 4px;
  border-right: 1px solid ${Colors.gray200};
  display: flex;
  align-items: center;
  justify-content: ${({ align }) => align || 'center'};
  font-size: 11px;

  &:last-child {
    border-right: none;
  }
`;

const ItemTableRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${Colors.gray200};
  min-height: 32px;

  &:last-child {
    border-bottom: none;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${Colors.gray300};
`;

const SummaryCell = styled.div<{ flex?: number; header?: boolean }>`
  flex: ${({ flex }) => flex || 1};
  padding: 8px;
  border-right: 1px solid ${Colors.gray200};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ header }) => (header ? Colors.gray50 : Colors.white)};
  font-size: 11px;

  &:last-child {
    border-right: none;
  }
`;

const formatBusinessNo = (businessNo?: string) => {
  if (!businessNo) return '-';
  const cleaned = businessNo.replace(/-/g, '');
  if (cleaned.length !== 10) return businessNo;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
};

const HometaxInvoiceFormatModal: React.FC<Props> = ({
  supplierInfo,
  contractorInfo,
  itemList,
  approvalNo,
  writtenDate,
  supplyValue,
  taxAmount,
  totalAmount,
  type,
  taxationType,
  modificationReason,
  remark,
  purposeType,
  ...props
}) => {
  // purposeType이 있으면 사용, 없으면 비움
  const purposeText =
    purposeType && (purposeType === 'RECEIPTS' || purposeType === 'BILLING')
      ? PurposeData[purposeType]
      : '';
  const formattedDate = dayjs(writtenDate).format('YYYY-MM-DD');
  const month = dayjs(writtenDate).format('MM');
  const day = dayjs(writtenDate).format('DD');

  return (
    <Modal
      title={
        <Flex align="center" gap={8}>
          <Text textStyle="16_600">홈택스 양식 미리보기</Text>
        </Flex>
      }
      width={800}
      footer={null}
      {...props}
    >
      <Container>
        {/* Header */}
        <Header>
          <TitleSection>
            <Text textStyle="18_600" style={{ color: Colors.black, letterSpacing: '8px' }}>
              전자세금계산서
            </Text>
          </TitleSection>
          <ApprovalSection>
            <Flex gap={8} align="center">
              <Text textStyle="12_500" style={{ color: Colors.gray600 }}>
                승인번호
              </Text>
              <Text textStyle="12_400">{approvalNo || '-'}</Text>
            </Flex>
          </ApprovalSection>
        </Header>

        {/* Supplier & Contractor Info */}
        <InfoRow>
          {/* Supplier (공급자) */}
          <SupplierSection>
            <InfoGrid>
              <SectionHeader color="#F5A3A8">공급자</SectionHeader>
              <InfoTable>
                <InfoTableRow>
                  <InfoLabel bgColor="#FFE5E3" textColor="#D13030">
                    등록번호
                  </InfoLabel>
                  <InfoValue>{formatBusinessNo(supplierInfo.businessRegNo)}</InfoValue>
                  <InfoLabel bgColor="#FFE5E3" textColor="#D13030">
                    종사업장번호
                  </InfoLabel>
                  <InfoValue>-</InfoValue>
                </InfoTableRow>
                <InfoTableRow>
                  <InfoLabel bgColor="#FFE5E3" textColor="#D13030">
                    <Flex direction="column" align="center" gap={0}>
                      <Text textStyle="11_400" style={{ color: '#D13030', fontWeight: 500 }}>
                        상호
                      </Text>
                      <Text textStyle="11_400" style={{ color: '#D13030', fontWeight: 500 }}>
                        (법인명)
                      </Text>
                    </Flex>
                  </InfoLabel>
                  <InfoValue>{supplierInfo.corpName || '-'}</InfoValue>
                  <InfoLabel bgColor="#FFE5E3" textColor="#D13030">
                    성명
                  </InfoLabel>
                  <InfoValue>{supplierInfo.corpCeo?.join(', ') || '-'}</InfoValue>
                </InfoTableRow>
                <InfoTableRow>
                  <InfoLabel bgColor="#FFE5E3" textColor="#D13030">
                    <Flex direction="column" align="center" gap={0}>
                      <Text textStyle="11_400" style={{ color: '#D13030', fontWeight: 500 }}>
                        사업장
                      </Text>
                      <Text textStyle="11_400" style={{ color: '#D13030', fontWeight: 500 }}>
                        주소
                      </Text>
                    </Flex>
                  </InfoLabel>
                  <InfoValue style={{ flex: 3 }}>
                    {[supplierInfo.corpAddress, supplierInfo.corpAddressDetail]
                      .filter(Boolean)
                      .join(' ') || '-'}
                  </InfoValue>
                </InfoTableRow>
                <InfoTableRow>
                  <InfoLabel bgColor="#FFE5E3" textColor="#D13030">
                    업태
                  </InfoLabel>
                  <InfoValue>{supplierInfo.businessType || '-'}</InfoValue>
                  <InfoLabel bgColor="#FFE5E3" textColor="#D13030">
                    종목
                  </InfoLabel>
                  <InfoValue>{supplierInfo.businessClass || '-'}</InfoValue>
                </InfoTableRow>
                <InfoTableRow>
                  <InfoLabel bgColor="#FFE5E3" textColor="#D13030">
                    이메일
                  </InfoLabel>
                  <InfoValue style={{ flex: 3 }}>
                    {supplierInfo.members?.[0]?.email || '-'}
                  </InfoValue>
                </InfoTableRow>
              </InfoTable>
            </InfoGrid>
          </SupplierSection>

          {/* Contractor (공급받는자) */}
          <ContractorSection>
            <InfoGrid>
              <SectionHeader color="#A3C0E8">공급받는자</SectionHeader>
              <InfoTable>
                <InfoTableRow>
                  <InfoLabel bgColor="#F5F5FF" textColor="#302DCF">
                    등록번호
                  </InfoLabel>
                  <InfoValue>{formatBusinessNo(contractorInfo.businessRegNo)}</InfoValue>
                  <InfoLabel bgColor="#F5F5FF" textColor="#302DCF">
                    종사업장번호
                  </InfoLabel>
                  <InfoValue>-</InfoValue>
                </InfoTableRow>
                <InfoTableRow>
                  <InfoLabel bgColor="#F5F5FF" textColor="#302DCF">
                    <Flex direction="column" align="center" gap={0}>
                      <Text textStyle="11_400" style={{ color: '#302DCF', fontWeight: 500 }}>
                        상호
                      </Text>
                      <Text textStyle="11_400" style={{ color: '#302DCF', fontWeight: 500 }}>
                        (법인명)
                      </Text>
                    </Flex>
                  </InfoLabel>
                  <InfoValue>{contractorInfo.corpName || '-'}</InfoValue>
                  <InfoLabel bgColor="#F5F5FF" textColor="#302DCF">
                    성명
                  </InfoLabel>
                  <InfoValue>{contractorInfo.corpCeo?.join(', ') || '-'}</InfoValue>
                </InfoTableRow>
                <InfoTableRow>
                  <InfoLabel bgColor="#F5F5FF" textColor="#302DCF">
                    <Flex direction="column" align="center" gap={0}>
                      <Text textStyle="11_400" style={{ color: '#302DCF', fontWeight: 500 }}>
                        사업장
                      </Text>
                      <Text textStyle="11_400" style={{ color: '#302DCF', fontWeight: 500 }}>
                        주소
                      </Text>
                    </Flex>
                  </InfoLabel>
                  <InfoValue style={{ flex: 3 }}>
                    {[contractorInfo.corpAddress, contractorInfo.corpAddressDetail]
                      .filter(Boolean)
                      .join(' ') || '-'}
                  </InfoValue>
                </InfoTableRow>
                <InfoTableRow>
                  <InfoLabel bgColor="#F5F5FF" textColor="#302DCF">
                    업태
                  </InfoLabel>
                  <InfoValue>{contractorInfo.businessType || '-'}</InfoValue>
                  <InfoLabel bgColor="#F5F5FF" textColor="#302DCF">
                    종목
                  </InfoLabel>
                  <InfoValue>{contractorInfo.businessClass || '-'}</InfoValue>
                </InfoTableRow>
                <InfoTableRow>
                  <InfoLabel bgColor="#F5F5FF" textColor="#302DCF">
                    이메일
                  </InfoLabel>
                  <InfoValue style={{ flex: 3 }}>
                    {contractorInfo.members?.[0]?.email || '-'}
                  </InfoValue>
                </InfoTableRow>
                <InfoTableRow>
                  <InfoLabel bgColor="#F5F5FF" textColor="#302DCF">
                    이메일
                  </InfoLabel>
                  <InfoValue style={{ flex: 3 }}>
                    {contractorInfo.members?.[1]?.email || '-'}
                  </InfoValue>
                </InfoTableRow>
              </InfoTable>
            </InfoGrid>
          </ContractorSection>
        </InfoRow>

        {/* Transaction Info */}
        <TransactionRow>
          <TransactionRowInner>
            <TransactionCell header>작성일자</TransactionCell>
            <TransactionCell header>공급가액</TransactionCell>
            <TransactionCell header>세액</TransactionCell>
            <TransactionCell header>수정사유</TransactionCell>
            <TransactionCell header flex={2}>
              비고
            </TransactionCell>
          </TransactionRowInner>
          <TransactionRowInner>
            <TransactionCell>{formattedDate}</TransactionCell>
            <TransactionCell>{(supplyValue ?? 0).toLocaleString()}</TransactionCell>
            <TransactionCell>
              {(taxAmount ?? 0).toLocaleString()}
              {taxationType && ` (${TaxationData[taxationType]})`}
            </TransactionCell>
            <TransactionCell>{modificationReason || '-'}</TransactionCell>
            <TransactionCell flex={2}>{remark || '-'}</TransactionCell>
          </TransactionRowInner>
        </TransactionRow>

        {/* Item Table */}
        <ItemTable>
          <ItemTableHeader>
            <ItemTableCell width="40px">월</ItemTableCell>
            <ItemTableCell width="40px">일</ItemTableCell>
            <ItemTableCell>품목</ItemTableCell>
            <ItemTableCell width="60px">규격</ItemTableCell>
            <ItemTableCell width="60px">수량</ItemTableCell>
            <ItemTableCell width="80px">단가</ItemTableCell>
            <ItemTableCell width="100px">공급가액</ItemTableCell>
            <ItemTableCell width="80px">세액</ItemTableCell>
            <ItemTableCell width="80px">비고</ItemTableCell>
          </ItemTableHeader>
          {itemList.length > 0 ? (
            itemList.map((item, index) => {
              const itemDate = item.date ? dayjs(item.date) : dayjs(writtenDate);
              return (
                <ItemTableRow key={index}>
                  <ItemTableCell width="40px">{itemDate.format('MM')}</ItemTableCell>
                  <ItemTableCell width="40px">{itemDate.format('DD')}</ItemTableCell>
                  <ItemTableCell>{item.name || '-'}</ItemTableCell>
                  <ItemTableCell width="60px">{item.unit || '-'}</ItemTableCell>
                  <ItemTableCell width="60px">
                    {item.quantity?.toLocaleString() || '-'}
                  </ItemTableCell>
                  <ItemTableCell width="80px">
                    {item.unitPrice?.toLocaleString() || '-'}
                  </ItemTableCell>
                  <ItemTableCell width="100px" align="flex-end">
                    {(item.supplyAmount ?? 0).toLocaleString()}
                  </ItemTableCell>
                  <ItemTableCell width="80px" align="flex-end">
                    {(item.taxAmount ?? 0).toLocaleString()}
                  </ItemTableCell>
                  <ItemTableCell width="80px">-</ItemTableCell>
                </ItemTableRow>
              );
            })
          ) : (
            <ItemTableRow>
              <ItemTableCell width="40px">{month}</ItemTableCell>
              <ItemTableCell width="40px">{day}</ItemTableCell>
              <ItemTableCell>-</ItemTableCell>
              <ItemTableCell width="60px">-</ItemTableCell>
              <ItemTableCell width="60px">-</ItemTableCell>
              <ItemTableCell width="80px">-</ItemTableCell>
              <ItemTableCell width="100px" align="flex-end">
                {(supplyValue ?? 0).toLocaleString()}
              </ItemTableCell>
              <ItemTableCell width="80px" align="flex-end">
                {(taxAmount ?? 0).toLocaleString()}
              </ItemTableCell>
              <ItemTableCell width="80px">-</ItemTableCell>
            </ItemTableRow>
          )}
        </ItemTable>

        {/* Summary */}
        <Flex>
          <Flex direction="column" style={{ flex: 1 }}>
            <SummaryRow>
              <SummaryCell header>합계금액</SummaryCell>
              <SummaryCell header>현금</SummaryCell>
              <SummaryCell header>수표</SummaryCell>
              <SummaryCell header>어음</SummaryCell>
              <SummaryCell header>외상미수금</SummaryCell>
            </SummaryRow>
            <SummaryRow style={{ borderBottom: 'none' }}>
              <SummaryCell>
                <Text textStyle="14_600">{(totalAmount ?? 0).toLocaleString()}</Text>
              </SummaryCell>
              <SummaryCell>-</SummaryCell>
              <SummaryCell>-</SummaryCell>
              <SummaryCell>-</SummaryCell>
              <SummaryCell>-</SummaryCell>
            </SummaryRow>
          </Flex>
          <Flex
            align="center"
            justify="center"
            style={{
              borderLeft: `1px solid ${Colors.gray200}`,
              padding: '8px 16px',
              minWidth: 120,
            }}
          >
            <Text textStyle="12_500">이 금액을 {purposeText ? `(${purposeText})함` : '함'}</Text>
          </Flex>
        </Flex>
      </Container>
    </Modal>
  );
};

export default HometaxInvoiceFormatModal;
